import { REVISION_STEP_LABELS, type RevisionAnalysis as Analysis } from "@kola/shared";
import { useEffect, useRef, useState } from "react";
import { analyzeCommentRevision, ApiError } from "../../core/api.js";
import type { BoardSession } from "../../core/session.js";

export function revisionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "revision-analysis-not-configured") return "Revision analysis is not available on this server yet.";
    if (error.code === "revision-context-changed") return "The comment or storyboard changed. Analyze again for a current result.";
    if (error.code === "revision-context-too-large") return "This thread and row contain too much text to analyze together.";
    if (error.status === 429) return "Analysis is busy or its request limit was reached. Try again later.";
    if (error.status === 403) return "An editor account is required to analyze revisions.";
    if (error.status === 404) return "This comment is unavailable, or the server needs the revision-analysis update.";
  }
  return "Revision analysis failed. Try again.";
}

/** On demand, local result only. Any board edit invalidates the suggestion. */
export function RevisionAnalysis({ session, threadId, disabled }: {
  session: BoardSession; threadId: string; disabled: boolean;
}) {
  const [result, setResult] = useState<Analysis | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const version = useRef(0);
  const mounted = useRef(false);
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const invalidate = () => { version.current++; setResult(null); setError(""); };
    const offCells = session.onCellsChanged(invalidate);
    const offState = session.store.subscribe((s, prev) => {
      if (s.commentsVersion !== prev.commentsVersion || s.structureVersion !== prev.structureVersion ||
          s.elementsVersion !== prev.elementsVersion || s.frozen !== prev.frozen) invalidate();
    });
    return () => { mounted.current = false; version.current++; offCells(); offState(); };
  }, [session, threadId]);

  async function analyze() {
    if (inFlight.current || disabled) return;
    inFlight.current = true;
    const before = version.current;
    setBusy(true); setResult(null); setError("");
    try {
      const answer = await analyzeCommentRevision(session.boardId, threadId);
      if (!mounted.current) return;
      if (version.current !== before || answer.threadId !== threadId) {
        setError("The comment or storyboard changed. Analyze again for a current result.");
      } else setResult(answer);
    } catch (err) {
      if (mounted.current) setError(revisionErrorMessage(err));
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <section className="comment-revision" aria-label="Revision analysis">
      <button className="btn" type="button" disabled={disabled || busy} onClick={() => void analyze()}>
        {busy ? "Analyzing revision…" : "Analyze revision"}
      </button>
      <div aria-live="polite">
        {error && <p role="alert">{error}</p>}
        {result && <>
          <p>{result.status === "no_change" ? "No production change detected." : result.status === "needs_review" ? "This interpretation needs review." : "Suggested production steps"}</p>
          {result.suggestedSteps.length > 0 && <ol>{result.suggestedSteps.map((step) => <li key={step}>{REVISION_STEP_LABELS[step]}</li>)}</ol>}
          <p className="cm-time">Jev analysis · Suggestions only; nothing has been changed.</p>
        </>}
      </div>
    </section>
  );
}
